'use client'

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'

import { loadConsent, saveConsent } from './consentStorage'
import { ALL_ACCEPTED, ConsentCategory, ConsentPreferences, CONSENT_STORAGE_KEY, DEFAULT_CONSENT } from './types'

interface CookieConsentContextValue {
  /** Current consent preferences (null = not yet decided) */
  preferences: ConsentPreferences | null
  /** Whether the banner should be shown */
  showBanner: boolean
  /** Whether the settings modal is open */
  showSettings: boolean
  /** Check if a specific category is consented */
  hasConsent: (category: ConsentCategory) => boolean
  /** Accept all cookie categories */
  acceptAll: () => void
  /** Reject all non-essential cookie categories */
  rejectAll: () => void
  /** Save custom preferences from the settings modal */
  savePreferences: (preferences: ConsentPreferences) => void
  /** Open the settings modal */
  openSettings: () => void
  /** Close the settings modal */
  closeSettings: () => void
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)
const CONSENT_CHANGE_EVENT = 'cookie-consent-change'

let cachedConsent: ConsentPreferences | null = null
let hasCachedConsent = false

function readConsentSnapshot(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null

  if (!hasCachedConsent) {
    cachedConsent = loadConsent()
    hasCachedConsent = true
  }

  return cachedConsent
}

function subscribeToConsentChanges(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const handleConsentChange = () => {
    cachedConsent = loadConsent()
    hasCachedConsent = true

    callback()
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === CONSENT_STORAGE_KEY) {
      handleConsentChange()
    }
  }

  window.addEventListener(CONSENT_CHANGE_EVENT, handleConsentChange)
  window.addEventListener('storage', handleStorageChange)

  return () => {
    window.removeEventListener(CONSENT_CHANGE_EVENT, handleConsentChange)
    window.removeEventListener('storage', handleStorageChange)
  }
}

function subscribeToClientReady(): () => void {
  return () => undefined
}

function getClientSnapshot(): boolean {
  return typeof window !== 'undefined'
}

function getServerSnapshot(): boolean {
  return false
}

/**
 * Updates Google Consent Mode v2 and dataLayer when consent changes.
 */
function updateGoogleConsentMode(preferences: ConsentPreferences): void {
  if (typeof window === 'undefined') return

  const w = window as unknown as Record<string, unknown>
  const gtag = w.gtag as ((...args: unknown[]) => void) | undefined

  if (typeof gtag !== 'function') return

  gtag('consent', 'update', {
    ad_storage: preferences.marketing ? 'granted' : 'denied',
    ad_user_data: preferences.marketing ? 'granted' : 'denied',
    ad_personalization: preferences.marketing ? 'granted' : 'denied',
    analytics_storage: preferences.analytical ? 'granted' : 'denied',
    functionality_storage: preferences.functional ? 'granted' : 'denied',
    personalization_storage: preferences.functional ? 'granted' : 'denied',
    security_storage: 'granted', // always allowed (necessary)
  })

  // Push consent update event to dataLayer for GTM
  const dataLayer = (w.dataLayer as unknown[]) || []
  dataLayer.push({
    event: 'cookie_consent_update',
    cookie_consent: { ...preferences },
  })
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const preferences = useSyncExternalStore(subscribeToConsentChanges, readConsentSnapshot, () => null)
  const isLoaded = useSyncExternalStore(subscribeToClientReady, getClientSnapshot, getServerSnapshot)
  const [showBanner, setShowBanner] = useState(true)
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    if (preferences) {
      updateGoogleConsentMode(preferences)
    }
  }, [preferences])

  const applyConsent = useCallback((newPreferences: ConsentPreferences) => {
    // Ensure necessary is always true
    const safe = { ...newPreferences, necessary: true }
    saveConsent(safe)
    cachedConsent = safe
    hasCachedConsent = true
    setShowBanner(false)
    setShowSettings(false)
    updateGoogleConsentMode(safe)

    // Dispatch custom event so analytics components can react
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: safe }))
  }, [])

  const acceptAll = useCallback(() => applyConsent(ALL_ACCEPTED), [applyConsent])
  const rejectAll = useCallback(() => applyConsent(DEFAULT_CONSENT), [applyConsent])
  const savePreferencesHandler = useCallback((p: ConsentPreferences) => applyConsent(p), [applyConsent])

  const openSettings = useCallback(() => {
    setShowSettings(true)
    setShowBanner(false)
  }, [])

  const closeSettings = useCallback(() => {
    setShowSettings(false)
    // If user hasn't consented yet, show banner again
    if (!preferences) {
      setShowBanner(true)
    }
  }, [preferences])

  const hasConsent = useCallback(
    (category: ConsentCategory): boolean => {
      if (category === 'necessary') return true

      return preferences?.[category] ?? false
    },
    [preferences]
  )

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      preferences,
      showBanner: isLoaded && showBanner && !preferences,
      showSettings,
      hasConsent,
      acceptAll,
      rejectAll,
      savePreferences: savePreferencesHandler,
      openSettings,
      closeSettings,
    }),
    [preferences, isLoaded, showBanner, showSettings, hasConsent, acceptAll, rejectAll, savePreferencesHandler, openSettings, closeSettings]
  )

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext)

  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }

  return context
}
